/**
 * Video fetch prefix from theme settings.
 *
 * Reads `window.theme.video_prefixes[placement]` (per-placement Cloudinary
 * fetch prefix tuned for that surface — e.g. smaller w_ for tiles). When the
 * placement has no configured prefix the URL is returned unchanged (no
 * interception). There is no global fallback — every surface must configure
 * its own placement prefix.
 *
 * Each configured prefix bakes in a fixed `w_<N>` delivery width chosen for the
 * surface (tile 600, pdp 1200, hero_desktop 1920, hero_mobile 900,
 * promo_tile 900, background 1280). N is roughly 2x the on-screen CSS width so
 * it stays sharp on retina (~2 physical pixels per CSS pixel). The width lives
 * in the prefix value so it's visible/editable in the Theme Editor. Browsers
 * don't support `srcset` / `sizes` on `<source>` inside `<video>`, so this
 * fixed-width prefix is how Windsor approximates responsive video.
 *
 * Configured in Theme Editor → Video Optimization.
 */

/**
 * Resolve the fetch prefix for a placement. Returns an empty string when no
 * placement is given or the placement has no configured prefix — there is no
 * global fallback, so callers then leave the URL untouched.
 *
 * @param {string} [placement]
 * @returns {string}
 */
export function getVideoUrlPrefix(placement) {
  if (!placement) return '';
  const theme = window.theme || {};
  const prefixes = theme.video_prefixes || {};
  return String(prefixes[placement] || '').trim();
}

export function toAbsoluteVideoUrl(url) {
  if (!url) return url;
  if (url.startsWith('https://')) return url;
  if (url.startsWith('//')) return `https:${url}`;
  if (url.startsWith('/')) return `${window.location.origin}${url}`;
  return url;
}

/**
 * Prepends the configured Cloudinary fetch prefix to `url`. No-op if no prefix
 * is configured. Idempotent — won't double-prefix.
 *
 * @param {string} url
 * @param {string} [placement] - placement key. With no placement, or a
 *   placement that has no configured prefix, the URL is returned unchanged.
 */
export function applyVideoFetchPrefix(url, placement) {
  const prefix = getVideoUrlPrefix(placement);
  if (!prefix || !url) return url;
  const absolute = toAbsoluteVideoUrl(url);
  // Cloudinary fetch returns 400 for HLS (.m3u8) playlists, so let Safari hit the raw Shopify URL.
  if (/\.m3u8(\?|$)/i.test(absolute)) return absolute;
  if (absolute.startsWith(prefix)) return absolute;
  // If the URL is already prefixed with any Cloudinary fetch base, leave it —
  // avoids double-fetching when it was already transformed at server-render
  // time (e.g. a different placement prefix applied in Liquid).
  if (absolute.includes('/video/fetch/')) return absolute;
  return prefix + absolute;
}
